import React, { useState } from 'lib/react';
import {
  EVButton,
  EVCheckbox, EVChip, EVGrid, EVInputLabel, EVTypography, makeStyles,
  EVDownloadIcon,
  EVCircularProgress,
} from 'lib/components';
import PropTypes from 'prop-types';
import { useTranslation } from 'lib/translation';
import { useSelector } from 'lib/redux';
import clsx from 'lib/clsx';
import { FILE_FORMAT_NAMES } from 'features/orderHistory/OrderHistory.constants';
import downloadReportFilesService from 'services/downloadReportFiles.service';
import { FILE_FORMATS, FILE_TYPES, PRODUCT_IDS } from 'constants/AppConstants';
import UpgradeButton from 'features/orderHistory/components/UpgradeButton.component';

export const useStyles = makeStyles((theme) => ({
  fileTypeRow: {
    padding: '5px',
    gap: '8px',
    '&:nth-of-type(even)': {
      backgroundColor: theme.evPalette.charcoal[100],
    },
    '& button': {
      '& span': {
        gap: '10px',
        boxSizing: 'content-box',
      },
      alignItems: 'center',
      fontWeight: 500,
      '&.singleDownloadBtn': {
        padding: '10px 16px 10px 12px',
        backgroundColor: theme.evPalette.salt[100],
        boxSizing: 'content-box',
      },
    },
    '&.downloadAll': {
      backgroundColor: theme.evPalette.ocean[100],
      padding: '8px',
    },
  },
  fileTypeName: {
    overflow: 'visible',
    whiteSpace: 'nowrap',
    fontWeight: 400,
    color: '#000000',
    '@media (max-width: 468px)': {
      maxWidth: '135px',
      whiteSpace: 'inherit',
      wordBreak: 'break-word',
    },
  },
  filesSelected: {
    overflow: 'visible',
    fontWeight: 500,
    color: '#000000',
    fontSize: '14px !important',
    letterSpacing: '0.28px',
  },
  unableToDownload: {
    overflow: 'visible',
    fontWeight: 400,
    color: '#BC1616',
    fontSize: '10px !important',
    letterSpacing: '0.2px',
  },
  esxFileRow: {
    padding: '12px',
    gap: '8px',
    backgroundColor: theme.evPalette.ocean[100],
    borderRadius: '2px',
  },
  esxFileLabel: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: '14px',
  },
  xactimateChip: {
    backgroundColor: '#A6D2FE',
    color: '#172554',
    fontWeight: 500,
    fontSize: '12px',
    height: '24px',
  },
  esxRowLeft: {
    padding: '0px 8px',
    gap: '8px',
    flex: 1,
    minWidth: 0,
  },
}));

function FilesWidget({
  reportId, selectedFiles, setSelectedFiles, handleShowEmailOptionsChange, primaryProductID,
  showFilesEsxUpgrade = false,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState({});
  const [downloadFailed, setDownloadFailed] = useState({});

  const reportFileTypes = useSelector(({ orderHistory: { fileTypes } }) => fileTypes?.[reportId]);
  const selectedFilesCount = Object.keys(selectedFiles).length;

  const isInformAdvanced = (primaryProductID === PRODUCT_IDS.EagleViewInformAdvanced)
    || (primaryProductID === PRODUCT_IDS.EagleViewInformAdvancedforCommercial);

  // #genai
  const addInformAdvanced = (reportFileDescription) => {
    const split = reportFileDescription.split('.');
    return `${split.slice(0, split.length - 1).join('.')}_InformAdvanced.${split[split.length - 1]}`;
  };

  // #genai
  const getDownloadFileText = (reportFileDescription, fileTypeID) => (
    isInformAdvanced && Number(fileTypeID) !== FILE_TYPES.EVMeasurementJSON
      ? addInformAdvanced(reportFileDescription)
      : reportFileDescription
  );

  const handleFileSelectChange = ({ target: { id } }, value) => {
    const selectedFilesList = { ...selectedFiles };

    const key = id.replace('select-', '');

    if (!selectedFilesList[key]) selectedFilesList[key] = value;
    else delete selectedFilesList[key];

    setSelectedFiles(selectedFilesList);
  };

  const handleAllFileSelect = ({ target: { checked } }) => {
    const selectedFilesList = {};

    if (checked && reportFileTypes) {
      reportFileTypes.forEach(({
        fileTypeName, fileFormatID, fileTypeID, id, reportFileDescription,
      }) => {
        // #genai
        selectedFilesList[id] = {
          fileTypeName, fileFormatID, fileTypeID, reportFileDescription,
        };
      });
    } else setDownloadFailed((prev) => ({ ...prev, selected: false }));

    setSelectedFiles(selectedFilesList);
  };

  const handleFilesDownload = (files, isSelectedFiles = false) => {
    if (!isSelectedFiles) setLoading((prev) => ({ ...prev, [files[0].fileID]: true }));
    else setLoading((prev) => ({ ...prev, selected: true }));
    downloadReportFilesService({ files }).then(({ ok }) => {
      if (!isSelectedFiles) setLoading((prev) => ({ ...prev, [files[0].fileID]: false }));
      else setLoading((prev) => ({ ...prev, selected: false }));

      if (!isSelectedFiles) setDownloadFailed((prev) => ({ ...prev, [files[0].fileID]: !ok }));
      else setDownloadFailed((prev) => ({ ...prev, selected: !ok }));
    });
  };

  const handleSelectedFilesDownload = () => {
    // #genai
    handleFilesDownload(Object.entries(selectedFiles).map(([fileId, { reportFileDescription, fileTypeID }]) => ({
      fileID: Number(fileId),
      reportID: reportId,
      fileText: getDownloadFileText(reportFileDescription, fileTypeID),
    })), true);
  };

  const hasReportFiles = (reportFileTypes?.length ?? 0) > 0;
  const hasEsxFormatFile = (reportFileTypes ?? []).some(
    ({ fileFormatID }) => Number(fileFormatID) === FILE_FORMATS.ESX,
  );
  if (!hasReportFiles && !showFilesEsxUpgrade) return null;

  return (
    <>
      <EVGrid>
        <EVTypography className="heading">{t('order_history__actions__files')}</EVTypography>
        {hasReportFiles && (
        <EVGrid container alignItems="center" wrap="nowrap">
          <EVCheckbox
            id={`selectAll-${reportId}`}
            checked={reportFileTypes.every(({ id }) => Boolean(selectedFiles[id]))}
            onChange={handleAllFileSelect}
            color="primary"
            style={{ fontSize: '24px !important' }}
          />
          <EVInputLabel htmlFor={`selectAll-${reportId}`} className={classes.fileTypeName}>
            {t('order_history__actions__select_all')}
          </EVInputLabel>
        </EVGrid>
        )}
        {hasReportFiles && reportFileTypes.map(({
          fileTypeName, fileFormatID, fileTypeID, id, reportFileDescription,
        }) => (
          <EVGrid key={id} container alignItems="center" justifyContent="space-between" wrap="nowrap" className={classes.fileTypeRow}>
            <EVGrid container alignItems="center" wrap="nowrap">
              <EVCheckbox
                id={`select-${id}`}
                checked={Boolean(selectedFiles[id])}
                onChange={(e) => handleFileSelectChange(e, {
                  // #genai
                  fileTypeName, fileFormatID, fileTypeID, reportFileDescription,
                })}
                color="primary"
                style={{ fontSize: '24px !important' }}
              />
              <EVInputLabel htmlFor={`select-${id}`} className={classes.fileTypeName}>
                {fileTypeName}
                {downloadFailed[id] && (
                <EVTypography noWrap className={classes.unableToDownload}>
                  {`${t('order_history_unable_to_download')}`}
                </EVTypography>
                )}
              </EVInputLabel>
            </EVGrid>
            <EVButton
              variant="text"
              color="primary"
              onClick={() => handleShowEmailOptionsChange({ [id]: { fileTypeName, fileFormatID } })}
              id={`email-${id}`}
            >
              {t('order_history__actions__email')}
            </EVButton>
            <EVButton
              variant="outlined"
              color="primary"
              className="singleDownloadBtn"
              id={`download-${id}`}
              onClick={() => handleFilesDownload([{
                fileID: Number(id),
                reportID: reportId,
                // #genai
                fileText: getDownloadFileText(reportFileDescription, fileTypeID),
              }])}
            >
              {isLoading[id] ? <EVCircularProgress color="inherit" size={24} /> : <EVDownloadIcon />}
              {FILE_FORMAT_NAMES[fileFormatID]}
            </EVButton>
          </EVGrid>
        ))}
        {showFilesEsxUpgrade && !hasEsxFormatFile && (
        <EVGrid
          container
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
          className={classes.esxFileRow}
        >
          <EVGrid container alignItems="center" wrap="wrap" className={classes.esxRowLeft}>
            <EVTypography component="span" className={classes.esxFileLabel}>
              {t('order_history__actions__esx_file')}
            </EVTypography>
            <EVChip size="small" label={t('order_history__actions__xactimate')} className={classes.xactimateChip} />
          </EVGrid>
          <UpgradeButton reportId={reportId} upgradeESX />
        </EVGrid>
        )}
      </EVGrid>
      {selectedFilesCount > 0 && (
      <EVGrid container alignItems="center" justifyContent="space-between" wrap="nowrap" className={clsx(classes.fileTypeRow, 'downloadAll')}>
        <EVTypography noWrap className={classes.filesSelected}>
          {`${selectedFilesCount} ${t('order_history__actions__files_selected')}`}
          {downloadFailed.selected && (
          <EVTypography noWrap className={classes.unableToDownload}>
            {`${t('order_history_unable_to_download')}`}
          </EVTypography>
          )}
        </EVTypography>
        <EVButton
          variant="contained"
          color="primary"
          id={`downloadSelected-${reportId}`}
          onClick={handleSelectedFilesDownload}
        >
          {isLoading.selected ? <EVCircularProgress color="inherit" size={24} /> : <EVDownloadIcon />}
          {t('order_history__actions__download')}
        </EVButton>
      </EVGrid>
      )}
    </>
  );
}

FilesWidget.defaultProps = {
  showFilesEsxUpgrade: false,
};

FilesWidget.propTypes = {
  reportId: PropTypes.number.isRequired,
  handleShowEmailOptionsChange: PropTypes.func.isRequired,
  setSelectedFiles: PropTypes.func.isRequired,
  selectedFiles: PropTypes.shape().isRequired,
  primaryProductID: PropTypes.number.isRequired,
  showFilesEsxUpgrade: PropTypes.bool,
};

export default FilesWidget;
